import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Modal, Spin, message, Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import type { FormInstance } from "antd";
import {
  getSupplierUserInfo,
  generateProcessCode,
  checkFirstPurchase,
  getExchangeRate,
  startMeetingApproval,
  getPriceApplicationById,
  approvalPass,
  getHistoryByInsId,
} from "@/apis/supplier";
import DynamicForm from "@/components/DynamicForm";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import ProcessTimeline from "@/components/ProcessTimeline";
import type { ProcessTimelineItem } from "@/components/ProcessTimeline";
import QuotationModal from "./components/QuotationModal";
import {
  getApplicantInfo,
  getUserDataFromSession,
} from "@/pages/supplier/SupplierProfile/utils/fillFormFields";
import { getPriceApplicationFormItems } from "./config/formConfig";
import type {
  PriceApplicationModalProps,
  SupplierUserInfo,
  MaterialRecord,
} from "./types";
import type { SupplierUserSimpleListItem } from "@/components/supplier/SelectSupplierModal/types";
import type { QuotationFileRecord } from "./components/QuotationModal";
import styles from "./index.module.less";

// ==================== 常量配置 ====================

const FORM_CONFIG = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  layout: "horizontal" as const,
  colon: false,
};

// ==================== 渲染函数 ====================

/**
 * 获取价格申请Modal标题
 */
const getModalTitle = (
  t: (key: string) => string,
  processCode: string = "",
) => {
  return (
    <div className={styles.titleContainer}>
      <span>{t("供应商价格申请表")}</span>
      <div className={styles.titleDivider} />
      <span className={styles.titleProcessCode}>
        {t("单号")}：{processCode || t("加载中")}
      </span>
    </div>
  );
};

// ==================== 主组件 ====================

const PriceApplicationModal: React.FC<PriceApplicationModalProps> = ({
  visible,
  onClose,
  supplierName,
  supplierCode,
  supplierUsName,
  processCode: externalProcessCode = "",
  loading: externalLoading = false,
  isEdit = false,
  applicationId,
  taskId,
  isView = false,
  insId,
  viewStatus,
  onRefresh,
  navigate,
}) => {
  const { t } = useTranslation("supplierProfile");
  const formRef = useRef<FormInstance>(null);
  const [loading, setLoading] = useState(false);
  const [supplierInfo, setSupplierInfo] = useState<SupplierUserInfo | null>(
    null,
  );
  const [processCode, setProcessCode] = useState<string>(externalProcessCode);
  const [supplierUserId, setSupplierUserId] = useState<string>("");
  const [currentTaskId, setCurrentTaskId] = useState<string | undefined>(
    taskId,
  );
  const [applicationDataId, setApplicationDataId] = useState<string | number>(
    "",
  );
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [factoryModalOpen, setFactoryModalOpen] = useState(false);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [applicationType, setApplicationType] = useState<string>();
  const [selectedQuotations, setSelectedQuotations] = useState<
    QuotationFileRecord[]
  >([]);
  const [materialData, setMaterialData] = useState<MaterialRecord[]>([]);
  // 流程动态状态
  const [timelineData, setTimelineData] = useState<ProcessTimelineItem[]>([]);
  // 是否来自工作台
  const [isFromWorkbench, setIsFromWorkbench] = useState<boolean>(false);
  // 首次采购冲突错误状态
  const [firstPurchaseConflictError, setFirstPurchaseConflictError] =
    useState<boolean>(false);

  // 当弹窗打开或 supplierCode 变化时，获取供应商信息或编辑数据
  useEffect(() => {
    if (!visible) {
      return;
    }

    // 判断是否来自工作台（有 taskId 表示来自工作台）
    setIsFromWorkbench(!!taskId);

    // 编辑模式或查看模式：获取价格申请详情
    if ((isEdit || isView) && applicationId) {
      const fetchApplicationDetail = async () => {
        try {
          setLoading(true);
          const response = await getPriceApplicationById(applicationId);

          if (response.data.code === 200 && response.data.data) {
            const applicationData = response.data.data;
            const materialList =
              applicationData.srmSupplierPriceApplicationMaterialDetailList ||
              [];

            // 字段映射转换：处理物料数据中的字段映射
            const mappedMaterialList = materialList.map((material: any) => ({
              ...material,
              // 将 newPriceInputType 映射到 currency（如果接口返回的字段名不一致）
              currency: material.currency || material.newPriceInputType,
            }));

            const formData = {
              ...applicationData,
              // 如果接口返回的是 supplierUserName，映射到表单的 supplierUsName
              supplierName: applicationData.supplierUserName,
              supplierUsName: applicationData.supplierUsUserName,
              applicantName: applicationData.purchaserUserNickname,
              purchaseUserNo: applicationData.purchaseUserUsername,
              createDate: applicationData.createTime,
              deptName: applicationData.purchaseUserDeptName,
              postName: applicationData.purchaseUserPostName,
              // 将 quotationNo 映射到 quotationFile 字段用于显示
              quotationFile: applicationData.quotationNo,
            };

            if (materialList && materialList.length > 0) {
              setMaterialData(mappedMaterialList);
            } else {
            }

            // 处理报价单数据：将 quotationNo 映射到 selectedQuotations 状态
            if (applicationData.quotationNo) {
              // 将逗号分隔的报价单号拆分为数组
              const quotationNos = applicationData.quotationNo
                .split(",")
                .map((no: string) => no.trim())
                .filter((no: string) => no);

              // 构建 selectedQuotations 数据结构
              const quotations = quotationNos.map((biddingNo: string) => ({
                biddingNo,
                id: "", // API 返回的数据中可能没有 id，这里设为空
              }));

              setSelectedQuotations(quotations);
            }
            // 编辑模式：来自工作台直接使用原单号，否则重新生成新单号；查看模式：使用原单号
            if (isEdit) {
              if (isFromWorkbench) {
                // 来自工作台：直接使用原单号
                setProcessCode(applicationData.processCode || "");
              } else {
                // 非工作台编辑：重新生成新单号
                setProcessCode(applicationData.processCode || "");
              }
            } else if (isView && applicationData.processCode) {
              // 查看模式：使用原单号
              setProcessCode(applicationData.processCode);
            }

            // 设置供应商用户ID（从接口返回的 supplierUserId）
            if (applicationData.supplierUserId) {
              setSupplierUserId(applicationData.supplierUserId);
            }

            // 设置应用数据ID（从接口返回的 id，用于编辑模式下调用 /view/pass 接口）
            if (applicationData.id) {
              setApplicationDataId(applicationData.id);
            }

            // 设置任务ID（从接口返回的 taskId，用于编辑模式下调用 /view/pass 接口）
            if (applicationData.taskId) {
              setCurrentTaskId(applicationData.taskId);
            } else {
            }

            // 填充表单数据
            formRef.current?.setFieldsValue(formData);
            message.success(t("数据加载成功"));

            // 获取历史记录（编辑模式和查看模式都需要显示流程动态）
            if (applicationData.insId || insId) {
              const historyInsId = applicationData.insId || insId;
              getHistoryByInsId(historyInsId)
                .then((historyResponse) => {
                  if (
                    historyResponse?.data?.code === 200 &&
                    Array.isArray(historyResponse?.data?.data)
                  ) {
                    const timeline = historyResponse.data.data.map(
                      (item: any) => ({
                        approver: item.approver,
                        flowStatus: item.flowStatus,
                        postName: item.postName,
                        createTime: item.createTime,
                        message: item.message,
                        avatarUrl:
                          item.avatarUrl ||
                          "https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg",
                      }),
                    );
                    setTimelineData(timeline);
                  }
                })
                .catch((historyError) => {});
            }
          } else {
            message.error(response.data.message || t("获取申请详情失败"));
          }
        } catch (error) {
          message.error(t("获取申请详情失败，请重试"));
        } finally {
          setLoading(false);
        }
      };

      fetchApplicationDetail();
      return;
    }

    // 新建模式：获取供应商信息

    // 如果没有 supplierCode，清空供应商信息并显示提示
    if (!supplierCode) {
      setSupplierInfo(null);
      return;
    }

    // 定义异步函数并立即执行
    const fetchSupplierData = async () => {
      try {
        setLoading(true);
        const response = await getSupplierUserInfo(supplierCode, 3);
        if (response.data.code === 200 && response.data.data) {
          const supplierData = response.data.data[0];
          setSupplierInfo(supplierData);
          // 从 API 返回的供应商信息中提取 supplierUserId
          if (supplierData?.id) {
            setSupplierUserId(supplierData.id);
          }
        } else {
          message.error(response.data.message || t("获取供应商信息失败"));
          setSupplierInfo(null);
        }
      } catch (error) {
        message.error(t("获取供应商信息失败，请重试"));
        setSupplierInfo(null);
      } finally {
        setLoading(false);
      }
    };

    // 生成单号（如果没有外部传入）
    const fetchProcessCode = async () => {
      try {
        const response = await generateProcessCode("JIA_GE_BIAN_GENG");
        let code = response?.data?.data;

        // 如果返回的数据包含"加载中"等文本，需要提取实际的单号
        if (typeof code === "string") {
          // 移除"单号："前缀和"加载中..."等文本，只保留实际的单号
          code = code.replace(/^[^A-Z0-9]*/, "").trim();
          if (code) {
            setProcessCode(code);
          }
        }
      } catch (error) {
        setProcessCode("生成失败");
      }
    };

    // 并行执行两个异步操作
    fetchSupplierData();
    // 来自工作台时不需要生成单号，直接使用外部传入的或保持空值
    if (!externalProcessCode && !taskId) {
      fetchProcessCode();
    } else if (externalProcessCode) {
      setProcessCode(externalProcessCode);
    }
  }, [
    visible,
    supplierCode,
    externalProcessCode,
    isEdit,
    isView,
    applicationId,
    insId,
    t,
    taskId,
  ]);

  const handleClose = useCallback(() => {
    formRef.current?.resetFields();
    setSupplierInfo(null);
    setProcessCode("");
    setSupplierUserId("");
    setApplicationDataId("");
    setCurrentTaskId(undefined);
    setSelectedQuotations([]);
    setMaterialData([]);
    setTimelineData([]);
    setIsFromWorkbench(false);
    setFirstPurchaseConflictError(false);
    onClose();
  }, [onClose]);

  // ==================== 工厂选择处理 ====================

  /**
   * 处理供应商选择
   * 将选中的供应商数据更新到表单中
   */
  const handleSupplierSelect = useCallback(
    (pickedSupplier?: SupplierUserSimpleListItem) => {
      if (pickedSupplier) {
        // 将选中的供应商信息填充到表单中
        formRef.current?.setFieldsValue({
          supplierCode: pickedSupplier.supplierCode || "",
          supplierName: pickedSupplier.supplierName || "",
          supplierUsName: pickedSupplier.supplierUsName || "",
        });
        // 更新 supplierUserId 状态，优先使用 id，其次使用 supplierCode
        setSupplierUserId(
          pickedSupplier.id || pickedSupplier.supplierCode || "",
        );
        message.success(t("供应商选择成功"));
      }
      setSupplierModalOpen(false);
    },
    [t],
  );

  /**
   * 打开供应商选择弹窗
   */
  const handleOpenSupplierModal = useCallback(() => {
    setSupplierModalOpen(true);
  }, []);

  /**
   * 处理工厂选择
   */
  const handleFactorySelect = useCallback(
    (pickedSupplier?: SupplierUserSimpleListItem) => {
      if (pickedSupplier) {
        // 将选中的工厂信息填充到表单中
        formRef.current?.setFieldValue(
          "factoryName",
          pickedSupplier.supplierCode || "",
        );
        message.success(t("工厂选择成功"));
      }
      setFactoryModalOpen(false);
    },
    [t],
  );

  /**
   * 处理获取物料
   * 从输入框获取物料编号，调用接口检查是否首次采购
   */
  const handleGetMaterial = useCallback(async () => {
    const materialCodeInput = document.getElementById(
      "materialCodeInput",
    ) as HTMLInputElement;
    const materialCode = materialCodeInput?.value?.trim();

    if (!materialCode) {
      message.error(t("请输入物料编号"));
      return;
    }

    const formValues = formRef.current?.getFieldsValue();
    const factoryName = formValues?.factoryName;
    const supplierCodeValue = formValues?.supplierCode;

    if (!factoryName) {
      message.error(t("请先选择工厂"));
      return;
    }

    if (!supplierCodeValue) {
      message.error(t("请先选择供应商"));
      return;
    }

    try {
      setLoading(true);
      const response = await checkFirstPurchase({
        fdFactory: factoryName,
        fdMaterialCode: materialCode,
        fdSupplierCode: supplierCodeValue,
      });

      if (response.data.code === 200) {
        const newIsFirstPurchase = response.data.data?.isFirstPurchase;

        // 根据接口返回的 isFirstPurchase 值判断是否显示错误提示
        if (newIsFirstPurchase === "YES") {
          setFirstPurchaseConflictError(true);
        }

        message.success(t("物料获取成功"));

        // 将返回的物料数据赋值给 materialData 状态
        if (
          response.data.data?.material &&
          Array.isArray(response.data.data.material)
        ) {
          setMaterialData(response.data.data.material);
        } else if (response.data.data?.material) {
          // 如果返回的是单个物料对象，转换为数组
          setMaterialData([response.data.data.material]);
        }

        // 将返回的 isFirstPurchase 值赋值给表单的 isFirstPurchaseValue 字段
        if (newIsFirstPurchase) {
          formRef.current?.setFieldValue(
            "isFirstPurchaseValue",
            newIsFirstPurchase,
          );
        }
      } else {
        message.error(response.data.message || t("获取物料失败"));
      }
    } catch (error) {
      message.error(t("获取物料失败，请重试"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * 打开工厂选择弹窗
   */
  const handleOpenFactoryModal = useCallback(() => {
    setFactoryModalOpen(true);
  }, []);

  /**
   * 打开报价单选择弹窗
   */
  const handleOpenQuotationModal = useCallback(() => {
    setQuotationModalOpen(true);
  }, []);

  /**
   * 导航到价格对比页面
   * @param recordId - 报价单ID
   */
  const handleNavigateToPriceCompare = useCallback(
    (recordId: string | number) => {
      if (navigate) {
        handleClose();
        navigate(`/inquiryManagement/priceCompare?recordId=${recordId}`);
      }
    },
    [navigate, handleClose],
  );

  /**
   * 关闭报价单弹窗
   */
  const handleCloseQuotationModal = useCallback(() => {
    setQuotationModalOpen(false);
  }, []);

  /**
   * 处理报价单选择
   * 将选中的报价单信息保存并更新表单显示
   */
  const handleQuotationSelect = useCallback(
    (quotationFiles: QuotationFileRecord[]) => {
      setSelectedQuotations(quotationFiles);
      // 更新表单中报价单字段的值，显示所有选中询比单号（逗号分隔）
      const biddingNos = quotationFiles.map((q) => q.biddingNo).join(", ");
      formRef.current?.setFieldValue("quotationFile", biddingNos);
      // 设置 quotationNo 字段为选中的报价单号
      formRef.current?.setFieldValue("quotationNo", biddingNos);
    },
    [],
  );

  /**
   * 处理物料表格数据变化
   * 更新指定行指定字段的值
   * 如果更新的是新价格，且已有汇率，则自动计算HKD价格
   */
  const handleMaterialFieldChange = useCallback(
    (rowIndex: number, fieldName: string, value: any) => {
      setMaterialData((prevData) => {
        const newMaterialData = [...prevData];
        if (newMaterialData[rowIndex]) {
          newMaterialData[rowIndex] = {
            ...newMaterialData[rowIndex],
            [fieldName]: value,
          };

          // 如果更新的是新价格输入，且有汇率，则计算HKD价格
          if (
            fieldName === "newPriceInput" &&
            value &&
            newMaterialData[rowIndex].exchangeRate
          ) {
            const exchangeRate = newMaterialData[rowIndex].exchangeRate;
            const newPriceHkd = (Number(value) * exchangeRate).toFixed(2);
            newMaterialData[rowIndex].newPriceHkd = newPriceHkd;
          }
        }
        return newMaterialData;
      });
    },
    [],
  );

  /**
   * 处理删除物料行
   * @param rowIndex - 要删除的行索引
   */
  const handleDeleteMaterial = useCallback(
    (rowIndex: number) => {
      setMaterialData((prevData) => {
        const newMaterialData = prevData.filter(
          (_, index) => index !== rowIndex,
        );
        message.success(t("物料删除成功"));
        return newMaterialData;
      });
    },
    [t],
  );

  /**
   * 处理货币变化时的汇率查询
   * 获取指定货币与HKD的汇率，计算新价格（HKD）
   */
  const handleCurrencyChange = useCallback(
    async (rowIndex: number, currency: string) => {
      if (!currency || currency === "HKD") {
        // 如果是 HKD，则新价格（HKD）等于新价格，汇率为1
        setMaterialData((prevData) => {
          const newMaterialData = [...prevData];
          if (newMaterialData[rowIndex]) {
            newMaterialData[rowIndex] = {
              ...newMaterialData[rowIndex],
              exchangeRate: 1,
              newPriceHkd: newMaterialData[rowIndex].newPriceInput,
            };
          }
          return newMaterialData;
        });
        return;
      }

      try {
        setLoading(true);
        const response = await getExchangeRate(currency);

        if (response.data.code === 200 && response.data.data) {
          // 获取汇率
          const exchangeRate = response.data.data;

          // 使用函数式更新确保获取最新的 materialData
          setMaterialData((prevData) => {
            const newMaterialData = [...prevData];
            if (newMaterialData[rowIndex]) {
              const newPriceInput = newMaterialData[rowIndex].newPriceInput;
              // 将输入价格乘以汇率得到HKD价格
              const newPriceHkd = newPriceInput
                ? (Number(newPriceInput) * exchangeRate).toFixed(2)
                : "";

              newMaterialData[rowIndex] = {
                ...newMaterialData[rowIndex],
                exchangeRate,
                newPriceHkd,
              };
            }
            return newMaterialData;
          });
          message.success(t("获取汇率成功汇率") + exchangeRate);
        } else {
          message.error(response.data.message || t("获取汇率失败"));
        }
      } catch (error) {
        message.error(t("获取汇率失败，请重试"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  // 获取表单项配置
  const formItems = useMemo(
    () =>
      getPriceApplicationFormItems(
        t,
        supplierInfo,
        handleOpenSupplierModal,
        handleOpenFactoryModal,
        applicationType,
        handleOpenQuotationModal,
        handleGetMaterial,
        materialData,
        handleMaterialFieldChange,
        handleCurrencyChange,
        isView,
        handleDeleteMaterial,
        isFromWorkbench,
        handleNavigateToPriceCompare,
        selectedQuotations,
        firstPurchaseConflictError,
      ),
    [
      t,
      supplierInfo,
      handleOpenSupplierModal,
      handleOpenFactoryModal,
      applicationType,
      handleOpenQuotationModal,
      handleGetMaterial,
      materialData,
      handleMaterialFieldChange,
      handleCurrencyChange,
      isView,
      handleDeleteMaterial,
      isFromWorkbench,
      handleNavigateToPriceCompare,
      selectedQuotations,
      firstPurchaseConflictError,
    ],
  );

  // 准备表单初始值 - 合并供应商信息和申请人信息
  const formValue = useMemo(() => {
    const applicantInfo = getApplicantInfo();

    // 优先使用 API 获取的 supplierInfo，否则使用父组件传来的供应商信息
    const baseSupplierInfo = supplierInfo || {
      supplierCode: supplierCode,
      supplierName: supplierName,
      supplierUsName: supplierUsName,
      supplierUserId: "",
    };

    return {
      ...baseSupplierInfo,
      ...applicantInfo,
    };
  }, [supplierInfo, supplierCode, supplierName, supplierUsName]);

  // 监听申请类型字段变化
  const handleApplicationTypeChange = useCallback((value: string) => {
    setApplicationType(value);
    // 清空采购类型字段值
    formRef.current?.setFieldValue("purchaseTypeValue", undefined);
  }, []);

  // 生成 Modal 标题
  const modalTitle = useMemo(
    () => getModalTitle(t, processCode),
    [processCode, t],
  );

  // ==================== 按钮处理函数 ====================

  /**
   * 处理暂存操作
   */
  const handleSaveDraft = useCallback(async () => {
    try {
      const values = formRef.current?.getFieldsValue();
      // 获取 inquiryProcess 字段值
      const inquiryProcessElement = document.querySelector(
        'textarea[name="inquiryProcess"]',
      ) as HTMLTextAreaElement;
      const inquiryProcess = inquiryProcessElement?.value || "";

      const userData = getUserDataFromSession();
      const purchaserUserId = userData?.userId || "";
      const purchaserUserNickname = userData?.nickname || "";
      const purchaseUserUsername = userData?.username || "";
      const purchaseUserPostName = userData?.postName || "";
      const purchaseUserPostNo = userData?.postNo || "";
      const purchaseUserDeptName = userData?.deptName || "";
      const purchaseUserDeptNo = userData?.deptNo || "";
      const supplierUserName = values?.supplierName || "";
      // 优先使用状态中的 supplierUserId（编辑模式），其次使用 supplierInfo.supplierUserId（新建模式）
      const finalSupplierUserId =
        supplierUserId || supplierInfo?.supplierUserId || "";

      // 从 selectedQuotations 状态中获取 quotationNo
      const quotationNo = selectedQuotations.map((q) => q.biddingNo).join(", ");

      // 根据是否来自工作台构建不同的提交数据格式
      let newSubmitData: any;

      if (isFromWorkbench) {
        // 来自工作台：使用简化的数据格式
        const reviewOpinion = values?.reviewOpinion || "";
        const reviewComments = values?.reviewComments || "";

        // 根据审批意见设置 skipType：同意为 PASS，驳回为空字符串
        const skipType = reviewOpinion === "agree" ? "PASS" : "";

        newSubmitData = {
          skipType,
          message: reviewComments,
          flowCode: "JIA_GE_BIAN_GENG",
          variable: {
            businessId: applicationDataId || "",
          },
        };
      } else {
        // 非工作台：使用完整的数据格式
        const submitData = {
          ...values,
          processCode,
          inquiryProcess,
          quotationNo,
          purchaserUserId,
          purchaserUserNickname,
          purchaseUserUsername,
          purchaseUserPostName,
          purchaseUserDeptName,
          purchaseUserDeptNo,
          purchaseUserPostNo,
          supplierUserName,
          supplierUsUserName: values?.supplierUsName || "",
          // 确保 supplierUserId 不被表单值覆盖
          supplierUserId: finalSupplierUserId,
          applicantName: undefined,
          postName: undefined,
          supplierName: undefined,
          supplierUsName: undefined,
          deptName: undefined,
          srmSupplierPriceApplicationMaterialDetailList: materialData.map(
            (material) => {
              // 计算价格变动比率
              const priceChangeRatio =
                material.newPriceHkd && material.oldPriceHkd
                  ? (
                      (material.newPriceHkd / material.oldPriceHkd) *
                      100
                    ).toFixed(2)
                  : undefined;

              return {
                ...material,
                // 为空的 newPriceValidUntil 设置默认值
                newPriceValidUntil: material.newPriceValidUntil || "9999-12-31",
                // 添加计算后的价格变动比率
                ...(priceChangeRatio && { priceChangeRatio }),
              };
            },
          ),
          // 编辑模式下添加 id 字段
          ...(isEdit && applicationDataId && { id: applicationDataId }),
        };

        // 移除 undefined 字段
        Object.keys(submitData).forEach(
          (key) => submitData[key] === undefined && delete submitData[key],
        );

        // 构建暂存提交数据结构
        newSubmitData = {
          flowCode: "JIA_GE_BIAN_GENG",
          formNo: `${processCode}`,
          flowStatus: 0,
          variable: {
            supplierUserId: finalSupplierUserId,
            isFromWorkbench,
          },
          srmSupplierPriceApplicationDTO: submitData,
        };
      }

      // 判断是否为已驳回状态
      const isRejected = viewStatus === "已驳回";

      // 编辑模式且非驳回状态：调用 /view/pass 接口（对应 approvalPass）
      // 编辑模式且是驳回状态：调用 /view/start 接口（对应 startMeetingApproval，重新发起审批）
      // 新建模式：调用 /view/start 接口
      if (isEdit && currentTaskId && !isRejected) {
        // 编辑非驳回状态：调用 /view/pass（继续审批流程）
        await approvalPass(newSubmitData, currentTaskId);
      } else {
        // 已驳回状态或新建模式：调用 /view/start（重新发起审批）
        await startMeetingApproval(newSubmitData);
      }
      message.success(t("暂存成功"));
      onRefresh?.();
      handleClose();
    } catch (error) {
      message.error(t("暂存失败请重试"));
    }
  }, [
    materialData,
    processCode,
    supplierInfo,
    supplierUserId,
    handleClose,
    isEdit,
    currentTaskId,
    applicationDataId,
    viewStatus,
    isFromWorkbench,
    selectedQuotations,
    onRefresh,
    t,
  ]);

  /**
   * 处理提交操作
   */
  const handleSubmit = useCallback(async () => {
    try {
      const values = await formRef.current?.validateFields();
      // 获取 inquiryProcess 字段值
      const inquiryProcessElement = document.querySelector(
        'textarea[name="inquiryProcess"]',
      ) as HTMLTextAreaElement;
      const inquiryProcess = inquiryProcessElement?.value || "";

      // 从 sessionStorage 获取用户信息中的 purchaserUserId 和 nickname
      const userData = getUserDataFromSession();
      const purchaserUserId = userData?.userId || "";
      const purchaserUserNickname = userData?.nickname || "";
      const purchaseUserUsername = userData?.username || "";
      const purchaseUserPostName = userData?.postName || "";
      const purchaseUserPostNo = userData?.postNo || "";
      const purchaseUserDeptName = userData?.deptName || "";
      const purchaseUserDeptNo = userData?.deptNo || "";
      const supplierUserName = values?.supplierName || "";
      // 优先使用状态中的 supplierUserId（编辑模式），其次使用 supplierInfo.supplierUserId（新建模式）
      const finalSupplierUserId =
        supplierUserId || supplierInfo?.supplierUserId || "";

      // 从 selectedQuotations 状态中获取 quotationNo
      const quotationNo = selectedQuotations.map((q) => q.biddingNo).join(", ");

      // 根据是否来自工作台构建不同的提交数据格式
      let newSubmitData: any;

      if (isFromWorkbench) {
        // 来自工作台：使用简化的数据格式
        const reviewOpinion = values?.reviewOpinion || "";
        const reviewComments = values?.reviewComments || "";

        // 根据审批意见设置 skipType：同意为 PASS，驳回为空字符串
        const skipType = reviewOpinion === "agree" ? "PASS" : "";

        newSubmitData = {
          skipType,
          message: reviewComments,
          flowCode: "JIA_GE_BIAN_GENG",
          variable: {
            businessId: applicationDataId || "",
          },
        };
      } else {
        // 非工作台：使用完整的数据格式
        const submitData = {
          ...values,
          processCode,
          inquiryProcess,
          quotationNo,
          purchaserUserId,
          purchaserUserNickname,
          purchaseUserUsername,
          purchaseUserPostName,
          purchaseUserDeptName,
          purchaseUserDeptNo,
          purchaseUserPostNo,
          supplierUserName,
          supplierUsUserName: values?.supplierUsName || "",
          // 确保 supplierUserId 不被表单值覆盖
          supplierUserId: finalSupplierUserId,
          applicantName: undefined,
          postName: undefined,
          supplierName: undefined,
          supplierUsName: undefined,
          deptName: undefined,
          srmSupplierPriceApplicationMaterialDetailList: materialData.map(
            (material) => {
              // 计算价格变动比率
              const priceChangeRatio =
                material.newPriceHkd && material.oldPriceHkd
                  ? (
                      (material.newPriceHkd / material.oldPriceHkd) *
                      100
                    ).toFixed(2)
                  : undefined;

              return {
                ...material,
                // 为空的 newPriceValidUntil 设置默认值
                newPriceValidUntil: material.newPriceValidUntil || "9999-12-31",
                // 添加计算后的价格变动比率
                ...(priceChangeRatio && { priceChangeRatio }),
              };
            },
          ),
          // 编辑模式下添加 id 字段
          ...(isEdit && applicationDataId && { id: applicationDataId }),
        };

        // 移除 undefined 字段
        Object.keys(submitData).forEach(
          (key) => submitData[key] === undefined && delete submitData[key],
        );

        newSubmitData = {
          flowCode: "JIA_GE_BIAN_GENG",
          formNo: `${processCode}`,
          variable: {
            supplierUserId: finalSupplierUserId,
            isFromWorkbench,
          },
          srmSupplierPriceApplicationDTO: submitData,
        };
      }

      // 判断是否为已驳回状态
      const isRejected = viewStatus === "已驳回";

      // 编辑模式且非驳回状态：调用 /view/pass 接口（对应 approvalPass）
      // 编辑模式且是驳回状态：调用 /view/start 接口（对应 startMeetingApproval，重新发起审批）
      // 新建模式：调用 /view/start 接口
      if (isEdit && currentTaskId && !isRejected) {
        // 编辑非驳回状态：调用 /view/pass（继续审批流程）
        await approvalPass(newSubmitData, currentTaskId);
      } else {
        // 已驳回状态或新建模式：调用 /view/start（重新发起审批）
        await startMeetingApproval(newSubmitData);
      }
      message.success(t("提交成功"));
      onRefresh?.();
      handleClose();
    } catch (error) {
      // message.error(t("请先完成表单填写"));
    }
  }, [
    handleClose,
    materialData,
    processCode,
    supplierInfo,
    supplierUserId,
    isEdit,
    currentTaskId,
    applicationDataId,
    viewStatus,
    isFromWorkbench,
    selectedQuotations,
    onRefresh,
    t,
  ]);

  return (
    <Modal
      title={modalTitle}
      open={visible}
      width="1300px"
      centered
      style={{ top: 10, paddingBottom: 20 }}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
      }}
      onCancel={handleClose}
      footer={
        isFromWorkbench ? (
          // 来自工作台：显示简化的按钮（取消和提交）
          <div className={styles.modalFooter}>
            <Space size="middle">
              <Button onClick={handleClose}>{t("取消")}</Button>
              <Button type="primary" onClick={handleSubmit}>
                {t("提交")}
              </Button>
            </Space>
          </div>
        ) : isView ? (
          // 查看模式：只显示关闭按钮
          <div className={styles.modalFooter}>
            <Button onClick={handleClose}>{t("关闭")}</Button>
          </div>
        ) : (
          // 编辑模式：显示暂存和提交按钮
          // 已驳回状态下只显示提交按钮（重新发起审批）
          <div className={styles.modalFooter}>
            <Space size="middle">
              <Button onClick={handleClose}>{t("取消")}</Button>
              {viewStatus !== "已驳回" && (
                <Button onClick={handleSaveDraft}>{t("暂存")}</Button>
              )}
              <Button type="primary" onClick={handleSubmit}>
                {t("提交")}
              </Button>
            </Space>
          </div>
        )
      }
    >
      <Spin spinning={loading}>
        <div style={{ display: "flex", gap: "20px" }}>
          {isEdit || isView || (supplierCode && supplierName) ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <DynamicForm
                ref={formRef}
                formItem={formItems}
                formConfig={{
                  ...FORM_CONFIG,
                  onValuesChange: (changedValues: any) => {
                    if ("applyTypeValue" in changedValues) {
                      handleApplicationTypeChange(changedValues.applyTypeValue);
                    }
                  },
                }}
                formValue={formValue}
              />
            </div>
          ) : (
            <div style={{ textAlign: "center", color: "#999" }}>
              {t("暂无数据")}
            </div>
          )}

          {/* 右侧流程动态区域 - 编辑和查看模式且有历史数据时显示，草稿状态不显示 */}
          {(isView || isEdit) &&
            timelineData.length > 0 &&
            viewStatus !== "草稿" && (
              <div style={{ width: "320px", flexShrink: 0 }}>
                <div
                  style={{
                    marginBottom: "12px",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#1d2129",
                  }}
                >
                  {t("流程动态")}
                </div>
                <ProcessTimeline items={timelineData} />
              </div>
            )}
        </div>
      </Spin>

      {/* 供应商选择弹框 */}
      <SelectSupplierModal
        open={supplierModalOpen}
        onClose={handleSupplierSelect}
      />

      {/* 工厂选择弹框 */}
      <SelectSupplierModal
        open={factoryModalOpen}
        onClose={handleFactorySelect}
      />

      {/* 报价单选择弹框 */}
      <QuotationModal
        open={quotationModalOpen}
        onClose={handleCloseQuotationModal}
        onSelectQuotation={handleQuotationSelect}
      />
    </Modal>
  );
};

export default PriceApplicationModal;
